import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router"
import AppLayout from "../layouts/AppLayout.vue"
import { canAccessRoute } from "../services/auth"
import { useGlobalStore } from "@/stores/global-store"
import ConfigService from "@shared/helpers/configServices"

const baseRoutes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: AppLayout,
    redirect: { name: "home" },
    children: [
      {
        name: "home",
        path: "",
        meta: { displayName: "dashboard.menuName" },
        component: () => import("../pages/dashboard/Dashboard.vue"),
      },
      {
        name: "settings",
        path: "preferences",
        meta: { displayName: "settings.menuName" },
        component: () => import("../pages/common/Settings.vue"),
      },
      {
        name: "user-info",
        path: "user-info",
        meta: { displayName: "userInfo.menuName" },
        component: () => import("../pages/common/UserInfo.vue"),
      },
      {
        name: "release-notes",
        path: "release-notes",
        meta: { displayName: "releaseNotes.title" },
        component: () => import("../pages/common/ReleaseNotes.vue"),
      },
    ],
  },
  {
    path: "/admin",
    name: "admin",
    component: AppLayout,
    redirect: { name: "menu-list" },
    children: [
      {
        name: "menu-list",
        path: "menuHierarchy",
        component: () => import("../pages/admin/menuHierarchy/MenuHierarchyListPage.vue"),
      },
      {
        name: "menu-edit",
        path: "menuHierarchy/edit/:id?",
        meta: { parentRoute: "menu-list" },
        component: () => import("../pages/admin/menuHierarchy/MenuHierarchyEditPage.vue"),
      },
      {
        name: "notification-template-list",
        path: "notificationTemplate",
        component: () => import("../pages/admin/notificationTemplate/NotificationTemplateListPage.vue"),
      },
      {
        name: "notification-template-edit",
        path: "notificationTemplate/edit/:uniqueId?",
        meta: { parentRoute: "notification-template-list" },
        component: () => import("../pages/admin/notificationTemplate/NotificationTemplateEditPage.vue"),
      },
      {
        name: "department-list",
        path: "department",
        component: () => import("../pages/admin/department/DepartmentListPage.vue"),
      },
      {
        name: "businessArea-list",
        path: "businessArea",
        component: () => import("../pages/admin/businessArea/BusinessAreaListPage.vue"),
      },
      {
        name: "configuration-list",
        path: "configuration",
        component: () => import("../pages/admin/configuration/ConfigurationListPage.vue"),
      },
      {
        name: "configuration-edit",
        path: "configuration/edit/:uniqueId?",
        meta: { parentRoute: "configuration-list" },
        component: () => import("../pages/admin/configuration/ConfigurationEditPage.vue"),
      },
      {
        name: "widget-list",
        path: "widget",
        component: () => import("../pages/admin/widget/WidgetListPage.vue"),
      },
      {
        name: "widget-edit",
        path: "widget/edit/:uniqueId?",
        meta: { parentRoute: "widget-list" },
        component: () => import("../pages/admin/widget/WidgetEditPage.vue"),
      },
      {
        name: "widget-category-list",
        path: "widgetCategory",
        component: () => import("../pages/admin/widgetCategory/WidgetCategoryListPage.vue"),
      },
    ],
  },
  {
    path: "/report",
    name: "report",
    component: AppLayout,
    redirect: { name: "report-viewer" },
    children: [
      {
        name: "report-viewer",
        path: "viewer/:id",
        component: () => import("../pages/report/View.vue"),
      },
    ],
  },
  {
    name: "404",
    path: "/404",
    component: () => import("../pages/404.vue"),
  },
  {
    name: "401",
    path: "/401",
    component: () => import("../pages/401.vue"),
  },
  {
    name: "403",
    path: "/403",
    component: () => import("../pages/403.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: { name: "404" },
  },
]

async function loadRemoteRoutes(): Promise<RouteRecordRaw[]> {
  const remoteRoutes: RouteRecordRaw[] = []
  const remoteApps = ConfigService.Current.remoteApps.split(",").map((app: string) => app.trim())

  try {
    if (remoteApps.includes("jtp-policy")) {
      // Check if the remote module is available
      const remoteModule = await import("jtp-policy/routes")
      const policyRoutes = remoteModule?.default
      if (Array.isArray(policyRoutes)) {
        // Add remote routes as children of the main layout
        policyRoutes.forEach((route) => {
          if (route.children) {
            route.children.forEach((child: any) => {
              remoteRoutes.push({
                ...child,
                // Ensure proper path structure
                path: route.path ? `${route.path.slice(1)}/${child.path}` : child.path,
              })
            })
          } else {
            remoteRoutes.push(route)
          }
        })
      }
    }
  } catch (error: any) {
    console.warn("Failed to load remote routes from jtp-policy:", error.message || error)
    console.info("Continuing without remote routes. Make sure jtp-policy is running on the configured port.")
  }

  return remoteRoutes
}

// Create router factory function
export async function createAppRouter() {
  // Load remote routes first
  const remoteRoutes = await loadRemoteRoutes()
  // Merge base routes with remote routes
  const allRoutes = [...baseRoutes]

  // Add remote routes as children of the main layout
  const mainLayoutRoute = allRoutes.find((route) => route.path === "/")
  if (mainLayoutRoute && mainLayoutRoute.children) {
    mainLayoutRoute.children.push(...remoteRoutes)
  }

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      if (to.hash) {
        return { el: to.hash, behavior: "smooth" }
      } else {
        window.scrollTo(0, 0)
      }
    },
    routes: allRoutes,
  })

  router.beforeEach((to, from, next) => {
    const globalStore = useGlobalStore()
    const routeConfig = globalStore.menuHierarchy.find((r) => r.route === to.name)
    if (routeConfig) {
      if (!canAccessRoute(routeConfig.route || "")) {
        return next({ name: "401" })
      }
    }

    next()
  })

  return router
}

// For backward compatibility, export a default router (without remote routes)
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes: baseRoutes,
})

export default router
